import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import LabelsApiKey from './labels/LabelsApiKey';
import LabelsRender from './labels/LabelsRender';
import LabelsHistory from './labels/LabelsHistory';

import { getSessionIdToken } from './auth/apiAuth';

import axios from 'axios';

export default function Labels({ auth, initialTab = 0 }) {
    const [selectedProject, setSelectedProject] = useState(null);
    const [attachedProjects, setAttachedProjects] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const API_BASE = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        const projects = auth.userState.attached_labels_projects || [];
        setAttachedProjects(projects);
        if (projects.length === 1) {
            setSelectedProject(projects[0]);
        } else if (projects.length > 1 && initialTab !== 2) {
            setSelectedProject(projects[0]);
        }
    }, [auth.userState.attached_labels_projects, initialTab]);

    useEffect(() => {
        if (selectedProject) fetchTemplates();
    }, [selectedProject]);

    const fetchTemplates = async () => {
        setLoadingTemplates(true);
        try {
            const token = await getSessionIdToken();
            const { data } = await axios.get(`${API_BASE}/labels/templates`, {
                headers: { Authorization: token },
                params: { project_id: selectedProject }
            });
            setTemplates(data.templates || []);
        } catch (err) {
            setTemplates([]);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleChangeProject = (event, value) => {
        setSelectedProject(value);
    };

    const handleSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(s => ({ ...s, open: false }));
    };

    const getDeactivation = () => {
        if (auth.userState.isAdmin) return null;
        const tmpl = (templates || []).find(t => t.project_id === selectedProject);
        const d = tmpl?.deactivation;
        if (d && d.dateChecked && d.date && new Date(d.date).getTime() <= Date.now()) return d;
        return null;
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

            {/* Project selector - only for History page */}
            {initialTab === 2 && (
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        px: 4,
                        py: 1.5,
                        background: 'white',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Autocomplete
                        disablePortal
                        options={attachedProjects}
                        sx={{ width: 300 }}
                        clearOnEscape
                        value={selectedProject}
                        onChange={handleChangeProject}
                        size="small"
                        renderInput={(params) => <TextField {...params} label="Project" />}
                        getOptionLabel={(opt) => opt.split('/').slice(1).join('/') || opt}
                    />
                </Stack>
            )}

            {/* Content based on route */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>

                {initialTab === 0 && (
                    <LabelsApiKey
                        auth={auth}
                        selectedProject={selectedProject}
                        onSnackbar={handleSnackbar}
                        deactivation={getDeactivation()}
                    />
                )}
                {initialTab === 1 && (
                    <LabelsRender
                        auth={auth}
                        selectedProject={selectedProject}
                        templates={templates}
                        loadingTemplates={loadingTemplates}
                        onSnackbar={handleSnackbar}
                        deactivation={getDeactivation()}
                    />
                )}
                {initialTab === 2 && (
                    <>
                        {!selectedProject && (
                            <Box sx={{ textAlign: 'center', mt: 10, color: 'text.secondary' }}>
                                <Typography variant="h6">Select a project to get started</Typography>
                            </Box>
                        )}
                        {selectedProject && (
                            <LabelsHistory
                                auth={auth}
                                selectedProject={selectedProject}
                                templates={templates}
                                onSnackbar={handleSnackbar}
                            />
                        )}
                    </>
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={handleCloseSnackbar}
                    action={
                        <IconButton size="small" onClick={handleCloseSnackbar}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    }
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
